import React from 'react';
import PropTypes from 'prop-types';
import { ImpressionProvider, useImpression } from '@thd-olt-component-react/impression';
import { stripNonWordCharacters } from '../../utils/stringUtils';

/**
 * The container the banner reports itself under, provided here so the banner never takes its
 * container from whatever it happens to render inside.
 *
 * `id` is set explicitly so the package never fills in its `DEF_<name>` default, and it must be
 * present regardless: the package throws on a container with no id. `sectionSlotID` is blank
 * because the banner has none.
 *
 * `name` and `component` stay alphanumeric: the package strips non-word characters from both.
 */
const CONTAINER = {
  id: 'AiAssistedSearchBanner',
  name: 'AiAssistedSearchBanner',
  component: 'AiAssistedSearchBanner',
  type: 'content',
  position: '',
  sectionSlotID: ''
};

/**
 * Registers the banner with the observer and carries the `clickid`.
 *
 * Separate from `BannerImpression` because `useImpression` reads its container from context, which
 * a component cannot do for a provider it renders itself.
 *
 * The search term is stripped here: the package cleans `name` and `component` but never `id`.
 */
const ImpressedRoot = ({ anchor, children }) => {
  const { ref, clickID } = useImpression({
    data: {
      id: stripNonWordCharacters(anchor),
      name: 'AiAssistedSearchBanner',
      component: 'AiAssistedSearchBanner',
      type: 'content',
      position: 1
    }
  });

  // eslint-disable-next-line react/no-unknown-property
  return <div ref={ref} clickid={clickID}>{children}</div>;
};

ImpressedRoot.displayName = 'ImpressedRoot';

ImpressedRoot.propTypes = {
  anchor: PropTypes.string,
  children: PropTypes.node
};

export const BannerImpression = ({ anchor, children }) => (
  // A copy: the package fills and cleans the container it is handed in place.
  <ImpressionProvider data={{ ...CONTAINER }}>
    <ImpressedRoot anchor={anchor}>
      {children}
    </ImpressedRoot>
  </ImpressionProvider>
);

BannerImpression.displayName = 'BannerImpression';

BannerImpression.propTypes = {
  /**
   * Search term the banner was built for; reported as the impression's `id`, stripped to word
   * characters.
   */
  anchor: PropTypes.string,
  /** The banner layout to impress. */
  children: PropTypes.node
};
